
import { useState, useEffect } from 'react';
import { YoungQuestion, YoungAnswer, YoungSchemasResult } from '@/types/personality';
import { youngSchemasQuestions } from '@/data/youngSchemas';
import { calculateYoungSchemasScores } from '@/utils/youngSchemasScoring';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/hooks/use-toast';

interface UseYoungSchemasTestProps {
  initialAnswers?: YoungAnswer[];
}

export const useYoungSchemasTest = ({ 
  initialAnswers = [] 
}: UseYoungSchemasTestProps = {}) => {
  const { user } = useAuth();
  
  // Questions - validate they exist
  const [questions] = useState<YoungQuestion[]>(youngSchemasQuestions || []);
  
  // Current question index
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // User answers
  const [answers, setAnswers] = useState<YoungAnswer[]>(initialAnswers);
  
  // Test completion state
  const [isCompleted, setIsCompleted] = useState(false);
  
  // Test results
  const [testResults, setTestResults] = useState<YoungSchemasResult | null>(null);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Save state to avoid duplicate saves
  const [isSaving, setIsSaving] = useState(false);

  // Service availability - simplified check
  const serviceAvailable = questions.length > 0;

  // Calculate progress
  const progress = questions.length > 0 
    ? Math.round((answers.length / questions.length) * 100) 
    : 0;

  // Current question
  const currentQuestion = questions[currentIndex];

  // Centralized save results function with improved error handling
  const saveResultsToDatabase = async (results: YoungSchemasResult) => {
    if (!user || isSaving) {
      console.log('Cannot save: user not authenticated or already saving');
      return;
    }

    try {
      setIsSaving(true);
      console.log('Saving Young Schema results to database...');

      // Save Young Schema results directly
      const { data: youngResultsData, error: youngResultsError } = await supabase
        .from('young_schema_results')
        .insert({
          user_id: user.id,
          emotional_deprivation: results.emotional_deprivation,
          abandonment: results.abandonment,
          mistrust_abuse: results.mistrust_abuse,
          social_isolation: results.social_isolation,
          defectiveness: results.defectiveness,
          failure: results.failure,
          dependence: results.dependence,
          vulnerability: results.vulnerability,
          enmeshment: results.enmeshment,
          entitlement: results.entitlement,
          insufficient_self_control: results.insufficient_self_control,
          subjugation: results.subjugation,
          self_sacrifice: results.self_sacrifice,
          approval_seeking: results.approval_seeking,
          negativity: results.negativity,
          emotional_inhibition: results.emotional_inhibition,
          unrelenting_standards: results.unrelenting_standards,
          punitiveness: results.punitiveness
        })
        .select('id')
        .single();

      if (youngResultsError) {
        console.error('Error saving Young Schema results:', youngResultsError);
        throw new Error(`Erro ao salvar resultados: ${youngResultsError.message}`);
      }

      if (!youngResultsData) {
        throw new Error('No data returned from insert operation');
      }

      console.log('Young Schema results saved with ID:', youngResultsData.id);

      // Handle test type registration with better error handling
      try {
        // Check if test type exists
        const { data: testTypeData, error: testTypeQueryError } = await supabase
          .from('personality_test_types')
          .select('id')
          .eq('name', 'young-schemas')
          .maybeSingle();

        if (testTypeQueryError) {
          console.error('Error querying test type:', testTypeQueryError);
        }

        let testTypeId: string;

        if (!testTypeData) {
          // Create the test type if it doesn't exist
          const { data: newTestType, error: createTestTypeError } = await supabase
            .from('personality_test_types')
            .insert({
              name: 'young-schemas',
              description: 'Teste de Esquemas Iniciais Desadaptativos de Young'
            })
            .select('id')
            .single();

          if (createTestTypeError || !newTestType) {
            console.error('Error creating test type:', createTestTypeError);
            testTypeId = '';
          } else {
            testTypeId = newTestType.id;
          }
        } else {
          testTypeId = testTypeData.id;
        }

        // Register the test if we have a valid test type ID
        if (testTypeId) {
          const { error: testRegistrationError } = await supabase
            .from('personality_tests')
            .insert({
              user_id: user.id,
              test_type_id: testTypeId,
              result_reference_id: youngResultsData.id
            });

          if (testRegistrationError) {
            console.error('Error registering test:', testRegistrationError);
          }
        }

      } catch (registrationError) {
        console.error('Test registration failed but results are saved:', registrationError);
      }

      console.log('Young Schema results saved successfully');
      
      toast({
        title: "Resultados salvos com sucesso!",
        description: "Seus resultados do teste de Esquemas de Young foram salvos.",
      });

    } catch (error: any) {
      console.error('Error in saveResultsToDatabase:', error);
      toast({
        title: "Erro ao salvar resultados",
        description: error.message || "Ocorreu um erro ao salvar seus resultados.",
        variant: "destructive"
      });
      setError(error.message || "Erro ao salvar resultados");
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate results when test is completed
  useEffect(() => {
    if (answers.length === questions.length && questions.length > 0 && !testResults && !isCompleted) {
      try {
        console.log('Calculating Young Schema results...');
        const results = calculateYoungSchemasScores(questions, answers);
        setTestResults(results);
        setIsCompleted(true);
        
        // Save results to database
        saveResultsToDatabase(results);
      } catch (err: any) {
        console.error('Error calculating results:', err);
        setError(err.message || "Erro ao calcular resultados");
      }
    }
  }, [answers, questions, testResults, isCompleted]);

  // Get answer for current question if exists
  const getCurrentAnswer = () => {
    return answers.find(a => a.questionId === currentQuestion?.id)?.score || 0;
  };

  // Answer current question
  const answerQuestion = (score: number) => {
    if (!currentQuestion) return;
    
    // Find if this question was already answered
    const existingIndex = answers.findIndex(a => a.questionId === currentQuestion.id);
    
    // Create new answers array, either updating or adding the answer
    const newAnswers = [...answers];
    if (existingIndex >= 0) {
      newAnswers[existingIndex] = { questionId: currentQuestion.id, score };
    } else {
      newAnswers.push({ questionId: currentQuestion.id, score });
    }
    
    setAnswers(newAnswers);
    
    // Save to localStorage for persistence
    try {
      localStorage.setItem('youngSchemasAnswers', JSON.stringify(newAnswers));
    } catch (e) {
      console.error('Failed to save answers to localStorage', e);
    }
    
    // Move to next question if not at the end
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };
  
  // Go to previous question
  const previousQuestion = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };
  
  // Go to specific question
  const goToQuestion = (index: number) => {
    if (index >= 0 && index < questions.length) {
      setCurrentIndex(index);
    }
  };
  
  // Reset the test
  const resetTest = () => {
    setAnswers([]);
    setCurrentIndex(0);
    setIsCompleted(false);
    setTestResults(null);
    setError(null);
    setIsSaving(false);
    localStorage.removeItem('youngSchemasAnswers');
  };
  
  // Load saved answers from localStorage
  useEffect(() => {
    try {
      const savedAnswers = localStorage.getItem('youngSchemasAnswers');
      if (savedAnswers) {
        const parsedAnswers = JSON.parse(savedAnswers) as YoungAnswer[];
        setAnswers(parsedAnswers);
        
        // Set current index to the next unanswered question
        const nextIndex = parsedAnswers.length;
        if (nextIndex < questions.length) {
          setCurrentIndex(nextIndex);
        }
      }
    } catch (e) {
      console.error('Failed to load saved answers', e);
    }
  }, [questions.length]);

  // Set error if no questions are available
  useEffect(() => {
    if (questions.length === 0) {
      setError('Questões do teste não estão disponíveis. Tente recarregar a página.');
    }
  }, [questions.length]);
  
  return {
    questions,
    currentQuestion,
    currentIndex,
    answers,
    isCompleted,
    isLoading: isLoading || isSaving,
    error,
    progress,
    testResults,
    serviceAvailable,
    answerQuestion,
    previousQuestion,
    goToQuestion,
    resetTest,
    getCurrentAnswer
  };
};
